import { useCallback, useState, useEffect } from "react";
import "../styles/nimbly2.css";
import { useProject } from '../contexts/ProjectContext';
import { useEnvironmentConfigContext } from '../contexts/EnvironmentConfigContext';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { Button } from "@progress/kendo-react-buttons";
import { xIcon } from "@progress/kendo-svg-icons";
import loadinggif from "../assets/loading.gif";

const ExpandedStreetviewMeter = ({ item, closePopup }) => {
    const { selectedProject, GCP_Project, Dataset_ID } = useProject();
    const { environmentConfig } = useEnvironmentConfigContext();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [meterData, setMeterData] = useState(null);

    const handleTabSelect = (e) => {
        setActiveTab(e.selected);
    };

    useEffect(() => {
        const controller = new AbortController();
        
        const populateMeterPopupData = () => {
            if (!item?.info?.GlobalID) {
                return;
            }
            setLoading(true);

            fetch(`${process.env.REACT_APP_WEB_API_URL}/api-common/poles/MeterLookup`, {
                method: 'POST',
                headers: {
                    'frontendenvironmentconfig': environmentConfig,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    Dataset_ID: Dataset_ID,
                    GCP_Project: GCP_Project,
                    Project: selectedProject,
                    GlobalID: item.info.GlobalID
                }),
                signal: controller.signal,
            })
            .then((res) => res.json())
            .then((data) => {
                setMeterData(data);
                setLoading(false);
            })
            .catch((error) => {
                if (error.name === "AbortError") {
                    return;
                }
                setLoading(false);
            });
        }

        if (selectedProject && GCP_Project && Dataset_ID) {
            populateMeterPopupData();
        }

        return () => {
            controller.abort();
        };
    }, [item, selectedProject, GCP_Project, Dataset_ID, environmentConfig]);

    if (!item) {
        return (
            <div className="pole-details-container-loading">
                <div className="no-data-text">No Selected Meter</div>
            </div>
        ); 
    }

    if (loading) {
        return (
            <div className="pole-details-container-loading">
                <img className="nimbly2-map-loader-gif" src={loadinggif} alt="Loading..." />
            </div>
        );
    }

    const fields = meterData?.fieldDict || {};
    const highlightFields = ["Symbology", "Structure ID", "Alternate Structure ID", "Feeder ID"];
    
    return (
        <div className="pole-details-container">

            <div className="pole-photo-hero">
                <Button rounded={"full"} fillMode={"solid"} svgIcon={xIcon} themeColor={"secondary"} className='pole-details-container-close-button' onClick={closePopup} />
                {meterData?.photosDict && meterData.photosDict["Photo 1"] ? (
                    <a href={meterData.photosDict["Photo 1"]["Photo Path"]} target="_blank" rel="noreferrer">
                        <img src={meterData.photosDict["Photo 1"]["Photo Path"]} alt={meterData.photosDict["Photo 1"]['Photo Name'] || 'Meter location'} title={meterData.photosDict["Photo 1"]["Photo Name"]} />
                    </a>
                ) : (
                    <div className="no-photo-placeholder">No Photo Available</div>
                )}
            </div>

            <TabStrip selected={activeTab} onSelect={handleTabSelect}>
                <TabStripTab title="Overview">
                    <div className="dynamic-info-list">
                        {Object.keys(fields).length === 0 ? (
                            <div className="no-data-text">No Meter Data</div>
                        ) : (
                            <>
                                {fields["Symbology"] && (
                                    <div className="info-flex-row">
                                        <div className="info-label">Meter Status</div>
                                        <div className="info-value"><strong>{fields["Symbology"]}</strong></div>
                                    </div>
                                )}
                                {fields["Structure ID"] && (
                                    <div className="info-flex-row">
                                        <div className="info-label">Structure ID</div>
                                        <div className="info-value">{fields["Structure ID"]}</div>
                                    </div>
                                )}
                                {fields["Alternate Structure ID"] && (
                                    <div className="info-flex-row">
                                        <div className="info-label">Alt Structure ID</div>
                                        <div className="info-value">{fields["Alternate Structure ID"]}</div>
                                    </div>
                                )}
                                {fields["Feeder ID"] && (
                                    <div className="info-flex-row">
                                        <div className="info-label">Feeder ID</div>
                                        <div className="info-value">{fields["Feeder ID"]}</div>
                                    </div>
                                )}
                                
                                {(fields["Symbology"] || fields["Structure ID"] || fields["Alternate Structure ID"] || fields["Feeder ID"]) && (
                                    <div className="info-section-divider"></div>
                                )}
                                
                                {Object.entries(fields)
                                    .filter(([key]) => !highlightFields.includes(key)) 
                                    .map(([key, value]) => (
                                    <div className="info-flex-row" key={key}>
                                        <div className="info-label">{key}</div>
                                        <div className="info-value">{value ?? ''}</div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </TabStripTab>
                
                <TabStripTab title="Photos">
                    {Object.keys(meterData?.photosDict || {}).length === 0 ? (
                        <div className="no-data-text">No Photo Data</div>
                    ) : (
                        Object.keys(meterData.photosDict).map((photo) => (
                            <div key={photo} className="dynamic-info-list">
                                { photo !== "Photo 1" && <div className="info-section-divider"></div> } 
                                <div className="info-flex-centered-row">
                                    <a href={meterData.photosDict[photo]["Photo Path"]} target="_blank" rel="noreferrer">
                                        <img src={meterData.photosDict[photo]["Photo Path"]} alt={photo} title={meterData.photosDict[photo]["Photo Name"]} />
                                    </a>
                                </div>
                                <div className="info-flex-row">
                                    <div className="info-label">Global ID</div>
                                    <div className="info-value">{meterData.photosDict[photo]["GlobalID"] ?? ''}</div>
                                </div>
                                <div className="info-flex-row">
                                    <div className="info-label">Photo Name</div>
                                    <div className="info-value">{meterData.photosDict[photo]["Photo Name"] ?? ''}</div>
                                </div>
                                <div className="info-flex-row">
                                    <div className="info-label">Rel GlobalID</div>
                                    <div className="info-value">{meterData.photosDict[photo]["Rel GlobalID"] ?? ''}</div>
                                </div>
                            </div>
                        ))
                    )}
                </TabStripTab>
            </TabStrip>
        </div>
    );
};

export default ExpandedStreetviewMeter;